import React, { useState, useEffect } from 'react';
import { X, Tag, Trash2, Edit2, Check, AlertCircle } from 'lucide-react';
import ConfirmationModal from './ConfirmationModal';

/**
 * TagManagerModal Component
 * Modal for managing brand tags (view, rename, delete)
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {function} props.onClose - Callback to close the modal
 * @param {Array<{name: string, count: number}>} props.tags - Available tags with counts
 * @param {function} props.onRenameTag - Callback to rename a tag (oldName, newName) => Promise
 * @param {function} props.onDeleteTag - Callback to delete a tag (tagName) => Promise
 * @param {boolean} [props.isLoading=false] - Whether tags are loading
 */
const TagManagerModal = ({
  isOpen,
  onClose,
  tags = [],
  onRenameTag,
  onDeleteTag,
  isLoading = false
}) => {
  const [editingTag, setEditingTag] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [tagToDelete, setTagToDelete] = useState(null);
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setEditingTag(null);
      setEditValue('');
      setTagToDelete(null);
      setError(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleStartEdit = (tag) => {
    setEditingTag(tag.name);
    setEditValue(tag.name);
    setError(null);
  };

  const handleCancelEdit = () => {
    setEditingTag(null);
    setEditValue('');
    setError(null);
  };

  const handleSaveEdit = async () => {
    if (!editValue.trim()) {
      setError('Tag name cannot be empty');
      return;
    }

    const newName = editValue.toLowerCase().trim();
    if (newName === editingTag) {
      handleCancelEdit();
      return;
    }

    // Check if new name already exists
    if (tags.some(t => t.name.toLowerCase() === newName)) {
      setError('A tag with this name already exists');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      await onRenameTag(editingTag, newName);
      handleCancelEdit();
    } catch (err) {
      console.error('Failed to rename tag:', err);
      setError('Failed to rename tag. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!tagToDelete) return;

    setIsProcessing(true);
    try {
      await onDeleteTag(tagToDelete);
      setTagToDelete(null);
    } catch (err) {
      console.error('Failed to delete tag:', err);
      setError('Failed to delete tag. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  return (
    <>
      <div
        onClick={handleBackdropClick}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9998,
          padding: 'var(--space-4)'
        }}
      >
        <div
          style={{
            backgroundColor: 'var(--color-surface)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-6)',
            maxWidth: '500px',
            width: '100%',
            maxHeight: '80vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            position: 'relative'
          }}
        >
          {/* Close button */}
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: 'var(--space-4)',
              right: 'var(--space-4)',
              backgroundColor: 'transparent',
              border: 'none',
              color: 'var(--color-text-tertiary)',
              cursor: 'pointer',
              padding: 'var(--space-1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 'var(--radius-sm)',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--color-surface-secondary)';
              e.currentTarget.style.color = 'var(--color-text-primary)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = 'var(--color-text-tertiary)';
            }}
          >
            <X size={20} />
          </button>

          {/* Title */}
          <h2
            style={{
              margin: '0 0 var(--space-2) 0',
              fontSize: '1.5rem',
              fontWeight: 600,
              color: 'var(--color-text-primary)',
              paddingRight: 'var(--space-8)'
            }}
          >
            Manage Tags
          </h2>

          <p style={{
            margin: '0 0 var(--space-4) 0',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-secondary)'
          }}>
            Rename or delete tags for this brand. Changes affect all items with these tags.
          </p>

          {/* Error message */}
          {error && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              padding: 'var(--space-2) var(--space-3)',
              background: 'var(--error-100)',
              color: 'var(--error-700)',
              borderRadius: 'var(--radius-md)',
              marginBottom: 'var(--space-3)',
              fontSize: 'var(--font-size-sm)'
            }}>
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          {/* Tags list */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            marginBottom: 'var(--space-4)'
          }}>
            {isLoading ? (
              <div style={{
                padding: 'var(--space-8)',
                textAlign: 'center',
                color: 'var(--color-text-tertiary)'
              }}>
                Loading tags...
              </div>
            ) : tags.length === 0 ? (
              <div style={{
                padding: 'var(--space-8)',
                textAlign: 'center',
                color: 'var(--color-text-tertiary)'
              }}>
                <Tag size={32} style={{ marginBottom: 'var(--space-2)', opacity: 0.5 }} />
                <p style={{ margin: 0 }}>No tags yet</p>
                <p style={{ margin: 'var(--space-1) 0 0 0', fontSize: 'var(--font-size-sm)' }}>
                  Add tags to library items to organize them
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                {tags.map(tag => (
                  <div
                    key={tag.name}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-3)',
                      padding: 'var(--space-3)',
                      background: 'var(--color-bg-secondary)',
                      borderRadius: 'var(--radius-md)',
                      border: '1px solid var(--color-border-light)'
                    }}
                  >
                    <Tag size={16} color="var(--color-text-tertiary)" />

                    {editingTag === tag.name ? (
                      <input
                        type="text"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        autoFocus
                        disabled={isProcessing}
                        style={{
                          flex: 1,
                          padding: 'var(--space-1) var(--space-2)',
                          border: '1px solid var(--color-primary)',
                          borderRadius: 'var(--radius-sm)',
                          background: 'var(--color-bg-primary)',
                          color: 'var(--color-text-primary)',
                          fontSize: 'var(--font-size-sm)',
                          outline: 'none'
                        }}
                      />
                    ) : (
                      <span style={{ flex: 1, fontSize: 'var(--font-size-sm)' }}>
                        {tag.name}
                      </span>
                    )}

                    <span style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-text-tertiary)',
                      background: 'var(--color-bg-tertiary)',
                      padding: 'var(--space-1) var(--space-2)',
                      borderRadius: 'var(--radius-full)'
                    }}>
                      {tag.count} {tag.count === 1 ? 'item' : 'items'}
                    </span>

                    {editingTag === tag.name ? (
                      <div style={{ display: 'flex', gap: 'var(--space-1)' }}>
                        <button
                          onClick={handleSaveEdit}
                          disabled={isProcessing}
                          style={{
                            background: 'var(--color-primary)',
                            border: 'none',
                            padding: 'var(--space-1)',
                            borderRadius: 'var(--radius-sm)',
                            cursor: isProcessing ? 'wait' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white'
                          }}
                        >
                          <Check size={14} />
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          disabled={isProcessing}
                          style={{
                            background: 'var(--color-bg-tertiary)',
                            border: 'none',
                            padding: 'var(--space-1)',
                            borderRadius: 'var(--radius-sm)',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'var(--color-text-secondary)'
                          }}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: 'var(--space-1)' }}>
                        <button
                          onClick={() => handleStartEdit(tag)}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            padding: 'var(--space-1)',
                            borderRadius: 'var(--radius-sm)',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'var(--color-text-tertiary)',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'var(--color-bg-tertiary)';
                            e.currentTarget.style.color = 'var(--color-text-primary)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'transparent';
                            e.currentTarget.style.color = 'var(--color-text-tertiary)';
                          }}
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={() => setTagToDelete(tag.name)}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            padding: 'var(--space-1)',
                            borderRadius: 'var(--radius-sm)',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'var(--color-text-tertiary)',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'var(--error-100)';
                            e.currentTarget.style.color = 'var(--error-600)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'transparent';
                            e.currentTarget.style.color = 'var(--color-text-tertiary)';
                          }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Close button */}
          <button
            onClick={onClose}
            className="btn btn-secondary"
            style={{ width: '100%' }}
          >
            Done
          </button>
        </div>
      </div>

      {/* Delete confirmation modal */}
      <ConfirmationModal
        isOpen={!!tagToDelete}
        onClose={() => setTagToDelete(null)}
        onConfirm={handleConfirmDelete}
        title="Delete Tag?"
        message={`Are you sure you want to delete the tag "${tagToDelete}"? This will remove it from all items that use it.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </>
  );
};

export default TagManagerModal;
